import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../useAuth';
import type { MaintenanceFiltersData } from './useMaintenanceFiltersState';
import {
  LIFECYCLE_WATCH_SUBSTATUS_OPTIONS,
  type LifecycleStage,
  type LifecycleWatchSubstatus,
} from '@/constants/lifecycleStages';
import { MAINTENANCE_STATS_KEY } from '@/lib/queryKeys/maintenanceKeys';

export interface WatchSubstageStat {
  count: number;
  sum_m2: number;
}

export type WatchSubstagesMap = Record<LifecycleWatchSubstatus, WatchSubstageStat>;

export interface LifecycleStageStat {
  count: number;
  sum_hour: number;
  sum_m2: number;
  watch_substages?: WatchSubstagesMap;
}

export type MaintenanceStats = Record<LifecycleStage, LifecycleStageStat>;

const emptyWatchSubstages = (): WatchSubstagesMap =>
  Object.fromEntries(
    LIFECYCLE_WATCH_SUBSTATUS_OPTIONS.map((o) => [o.value, { count: 0, sum_m2: 0 }])
  ) as WatchSubstagesMap;

const EMPTY_STAT: LifecycleStageStat = { count: 0, sum_hour: 0, sum_m2: 0 };
const EMPTY_STATS: MaintenanceStats = {
  yet_transferred: { ...EMPTY_STAT, watch_substages: emptyWatchSubstages() },
  warranty: { ...EMPTY_STAT, watch_substages: emptyWatchSubstages() },
  watch: { ...EMPTY_STAT, watch_substages: emptyWatchSubstages() },
  maintenance: { ...EMPTY_STAT, watch_substages: emptyWatchSubstages() },
};

interface RPCResult {
  success: boolean;
  data?: Partial<MaintenanceStats>;
  error?: { message: string };
}

const toStat = (raw: any): LifecycleStageStat => ({
  count: Number(raw?.count ?? 0),
  sum_hour: Number(raw?.sum_hour ?? 0),
  sum_m2: Number(raw?.sum_m2 ?? 0),
});

const toWatchSubstages = (raw: any): WatchSubstagesMap => {
  const out = emptyWatchSubstages();
  for (const opt of LIFECYCLE_WATCH_SUBSTATUS_OPTIONS) {
    const r = raw?.[opt.value];
    if (r) {
      out[opt.value] = {
        count: Number(r.count ?? 0),
        sum_m2: Number(r.sum_m2 ?? 0),
      };
    }
  }
  return out;
};

export const useMaintenanceStats = (filters: MaintenanceFiltersData) => {
  const { user } = useAuth();

  return useQuery<MaintenanceStats>({
    queryKey: [
      MAINTENANCE_STATS_KEY,
      filters.searchQuery || '',
      filters.projectGroup || '',
      (filters.projectType || []).join(',') || '',
      filters.teamLead || '',
      filters.company || '',
      (filters.contractYear || []).join(',') || '',
    ],

    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_service_stats_by_lifecycle', {
        search_query: filters.searchQuery || null,
        project_group_filter: filters.projectGroup || null,
        project_type_filter:
          Array.isArray(filters.projectType) && filters.projectType.length > 0
            ? filters.projectType[0]
            : null,
        team_lead_filter: filters.teamLead || null,
        company_filter: filters.company || null,
        contract_year_filter: filters.contractYear?.length ? filters.contractYear : null,
      });

      if (error) throw error;
      const result = data as unknown as RPCResult;
      if (!result?.success) {
        throw new Error(result?.error?.message || 'Stats RPC failed');
      }
      const d = result.data || {};
      return {
        yet_transferred: {
          ...toStat(d.yet_transferred),
          watch_substages: toWatchSubstages((d.yet_transferred as any)?.substages),
        },
        warranty: {
          ...toStat(d.warranty),
          watch_substages: toWatchSubstages((d.warranty as any)?.substages),
        },
        watch: {
          ...toStat(d.watch),
          watch_substages: toWatchSubstages((d.watch as any)?.substages),
        },
        maintenance: {
          ...toStat(d.maintenance),
          watch_substages: toWatchSubstages((d.maintenance as any)?.substages),
        },
      };
    },
    placeholderData: EMPTY_STATS,
  });
};
